import { useQuery } from '@tanstack/react-query'
import { getEbayConnectionStatus, type EbayConnectionStatus } from '@/services/ebay'

/**
 * "Can this user publish a listing to eBay right now?"
 *
 * Centralizes the prerequisites every create-listing callsite needs to
 * surface to users *before* showing the Publish button:
 *
 *  1. eBay account connected (OAuth tokens present)
 *  2. Business policies cached (fulfillment + payment + return)
 *
 * Components consume `status` to render an inline "Connect eBay" or
 * "Set up your business policies" call to action, and `ready` to
 * gate the actual Publish button.
 *
 * Cached for 30 s so opening multiple drafts in a row doesn't re-fetch.
 */
export function useEbayPublishability() {
  const query = useQuery<EbayConnectionStatus>({
    queryKey: ['ebay-publishability'],
    queryFn: getEbayConnectionStatus,
    staleTime: 30_000,
  })

  const status = query.data
  const ready = Boolean(status?.connected && status?.policiesReady)

  let reason: 'not-connected' | 'policies-missing' | null = null
  if (status && !status.connected) reason = 'not-connected'
  else if (status && !status.policiesReady) reason = 'policies-missing'

  return {
    status,
    ready,
    reason,
    isLoading: query.isLoading,
    refetch: query.refetch,
  }
}
